class SizeGuideDrawer extends HTMLElement {
  constructor() {
    super()

    this.overlay = this.querySelector('.cart-drawer__overlay')
    this.inner = this.querySelector('.drawer__inner')
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'))
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'))
    this.isOpen = false
    this.trueToSizeLoaded = false

    this.close = this.close.bind(this)
    this.onKeyUp = this.onKeyUp.bind(this)
    this.onTabClick = this.onTabClick.bind(this)
    this.onTabKeydown = this.onTabKeydown.bind(this)
    this.onUnitClick = this.onUnitClick.bind(this)
    this.onUnitKeydown = this.onUnitKeydown.bind(this)
    this.onUnitTrackClick = this.onUnitTrackClick.bind(this)

    if (this.overlay) this.overlay.addEventListener('click', this.close)

    const closeBtn = this.querySelector('[data-size-guide-close]')
    if (closeBtn) closeBtn.addEventListener('click', this.close)

    this.addEventListener('keyup', this.onKeyUp)

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.onTabClick)
      tab.addEventListener('keydown', this.onTabKeydown)
    })

    this.bindUnitToggle()

    this.setAttribute('aria-hidden', 'true')
  }

  connectedCallback() {
    if (this.parentElement !== document.body) {
      document.body.appendChild(this)
    }
  }

  onKeyUp(event) {
    if (!this.isOpen) return
    if (event.code === 'Escape') this.close()
  }

  onTabClick(event) {
    const index = Number(event.currentTarget.dataset.tabIndex)
    if (!Number.isNaN(index)) this.selectTab(index)
  }

  onTabKeydown(event) {
    if (!this.tabs.length) return

    const currentIndex = this.tabs.indexOf(event.currentTarget)
    if (currentIndex < 0) return

    let nextIndex = currentIndex

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % this.tabs.length
      event.preventDefault()
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length
      event.preventDefault()
    } else if (event.key === 'Home') {
      nextIndex = 0
      event.preventDefault()
    } else if (event.key === 'End') {
      nextIndex = this.tabs.length - 1
      event.preventDefault()
    } else {
      return
    }

    this.selectTab(nextIndex)
    this.tabs[nextIndex].focus()
  }

  bindUnitToggle() {
    this.unitRoot = this.querySelector('[data-size-guide-unit]')
    if (!this.unitRoot) return

    this.unitButtons = Array.from(this.unitRoot.querySelectorAll('[data-unit]'))
    this.unitCharts = Array.from(this.querySelectorAll('[data-chart-unit]'))

    this.unitButtons.forEach((button) => {
      button.addEventListener('click', this.onUnitClick)
      button.addEventListener('keydown', this.onUnitKeydown)
    })

    const track = this.unitRoot.querySelector('[data-unit-track]')
    if (track) track.addEventListener('click', this.onUnitTrackClick)
  }

  onUnitClick(event) {
    this.selectUnit(event.currentTarget.dataset.unit)
  }

  onUnitTrackClick() {
    const current = this.unitRoot && this.unitRoot.dataset.activeUnit
    this.selectUnit(current === 'cm' ? 'in' : 'cm')
  }

  onUnitKeydown(event) {
    if (!this.unitButtons || !this.unitButtons.length) return

    let nextUnit = null

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextUnit = 'cm'
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextUnit = 'in'
    } else if (event.key === 'Home') {
      nextUnit = 'in'
    } else if (event.key === 'End') {
      nextUnit = 'cm'
    } else {
      return
    }

    event.preventDefault()
    this.selectUnit(nextUnit)
    const nextButton = this.unitButtons.find((button) => button.dataset.unit === nextUnit)
    if (nextButton) nextButton.focus()
  }

  selectUnit(unit) {
    if (!this.unitRoot || (unit !== 'in' && unit !== 'cm')) return

    this.unitRoot.dataset.activeUnit = unit

    this.unitButtons.forEach((button) => {
      const selected = button.dataset.unit === unit
      button.classList.toggle('is-active', selected)
      button.setAttribute('aria-pressed', selected ? 'true' : 'false')
    })

    this.unitCharts.forEach((chart) => {
      if (chart.dataset.chartUnit === unit) {
        chart.removeAttribute('hidden')
      } else {
        chart.setAttribute('hidden', '')
      }
    })
  }

  selectTab(index) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index
      tab.classList.toggle('is-active', selected)
      tab.setAttribute('aria-selected', selected ? 'true' : 'false')
      tab.tabIndex = selected ? 0 : -1
    })

    this.panels.forEach((panel, i) => {
      const selected = i === index
      panel.classList.toggle('is-active', selected)
      if (selected) {
        panel.removeAttribute('hidden')
      } else {
        panel.setAttribute('hidden', '')
      }
    })
  }

  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy

    this.loadTrueToSize()

    if (this.isOpen) return
    this.isOpen = true
    this.setAttribute('aria-hidden', 'false')

    setTimeout(() => {
      this.classList.add('animate', 'active')
    })

    this.addEventListener(
      'transitionend',
      () => {
        if (!this.isOpen) return
        const containerToTrapFocusOn = this.inner || this
        const focusElement =
          this.querySelector('.size-guide-drawer__tab.is-active') ||
          this.querySelector('.drawer__close') ||
          containerToTrapFocusOn
        if (typeof trapFocus === 'function') trapFocus(containerToTrapFocusOn, focusElement)
      },
      { once: true }
    )

    if (typeof lockDrawerScroll === 'function') lockDrawerScroll()
  }

  close() {
    if (!this.isOpen) return
    this.isOpen = false
    this.classList.remove('active')
    this.setAttribute('aria-hidden', 'true')
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement)
    if (typeof unlockDrawerScroll === 'function') unlockDrawerScroll()
  }

  async loadTrueToSize() {
    if (this.trueToSizeLoaded) return
    this.trueToSizeLoaded = true

    const root = this.querySelector('[data-size-guide-tts]')
    if (!root) return

    const appKey = root.dataset.appKey
    const productId = root.dataset.productId
    if (!appKey || !productId) return

    try {
      const response = await fetch(
        `https://api-cdn.yotpo.com/v1/widget/${encodeURIComponent(appKey)}/products/${encodeURIComponent(
          productId
        )}/reviews.json?per_page=1`
      )
      if (!response.ok) return

      const data = await response.json()
      const bottomline = data && data.response && data.response.bottomline
      const fields = bottomline && bottomline.custom_fields_bottomline
      const total = Number(bottomline && bottomline.total_review)
      if (!fields || typeof fields !== 'object' || !(total > 0)) return

      const fit = Object.values(fields).find(
        (field) => field && (field.type === 'Size' || field.title === 'Fit Advice')
      )
      if (!fit || fit.score == null || Number.isNaN(Number(fit.score))) return

      const min = 1
      const max = 3
      const score = Math.min(max, Math.max(min, Number(fit.score)))
      const position = ((score - min) / (max - min)) * 100
      const rounded = Math.min(max, Math.max(min, Math.round(score)))
      const labels = {
        1: root.dataset.labelSizeDown,
        2: root.dataset.labelTrueToSize,
        3: root.dataset.labelSizeUp,
      }
      const valueText = labels[rounded] || labels[2]
      const valueEl = root.querySelector('[data-tts-value]')
      const countEl = root.querySelector('[data-tts-count]')

      if (valueEl) valueEl.textContent = valueText
      root.style.setProperty('--tts-position', `${position}%`)

      if (countEl) {
        const formatted = total.toLocaleString(root.dataset.locale || undefined)
        countEl.textContent = (root.dataset.countTemplate || '').replace('__COUNT__', formatted)
      }

      const customersSay = root.dataset.customersSay || ''
      const summary = [customersSay, valueText, countEl && countEl.textContent]
        .filter(Boolean)
        .join(' ')
      if (summary) root.setAttribute('aria-label', summary)

      root.hidden = false
    } catch (error) {
      this.trueToSizeLoaded = false
    }
  }
}

class SizeGuideDrawerOpener extends HTMLElement {
  constructor() {
    super()

    const button = this.querySelector('button')
    if (!button) return

    button.addEventListener('click', () => {
      const drawerId = this.getAttribute('data-drawer')
      const drawer = drawerId && document.getElementById(drawerId)
      if (drawer && typeof drawer.open === 'function') drawer.open(button)
    })
  }
}

if (!customElements.get('size-guide-drawer')) {
  customElements.define('size-guide-drawer', SizeGuideDrawer)
}

if (!customElements.get('size-guide-drawer-opener')) {
  customElements.define('size-guide-drawer-opener', SizeGuideDrawerOpener)
}
